import { forwardRef, type HTMLAttributes } from 'react';
import { cn } from '@/lib/utils';

export interface CardProps extends HTMLAttributes<HTMLDivElement> {
  variant?: 'default' | 'bordered' | 'elevated' | 'glass' | 'ghost';
  padding?: 'none' | 'xs' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  glow?: boolean;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
  ({ className, variant = 'default', padding = 'md', hover = false, glow = false, ...props }, ref) => {
    const variants = {
      default: cn(
        // Light mode
        'bg-white border border-gray-200',
        // Dark mode
        'dark:bg-secondary-900/80 dark:border-white/[0.06]',
        'dark:shadow-[0_0_0_1px_rgba(255,255,255,0.02),0_1px_2px_rgba(0,0,0,0.3)]'
      ),
      bordered: cn(
        'bg-white border-2 border-gray-200',
        'dark:bg-secondary-900/60 dark:border-white/[0.1]'
      ),
      elevated: cn(
        'bg-white shadow-lg border-0',
        'dark:bg-secondary-900/90 dark:shadow-elevated dark:border dark:border-white/[0.05]'
      ),
      glass: cn(
        'backdrop-blur-xl',
        // Light mode
        'bg-white/70 border border-gray-200/50',
        // Dark mode
        'dark:bg-secondary-900/60 dark:border-white/[0.08]',
        'dark:shadow-glass',
        'dark:bg-gradient-to-br dark:from-white/[0.04] dark:to-white/[0.01]'
      ),
      ghost: cn(
        'bg-transparent border-0',
        'dark:bg-transparent'
      ),
    };

    const paddings = {
      none: '',
      xs: 'p-2',
      sm: 'p-3',
      md: 'p-4',
      lg: 'p-5',
    };

    return (
      <div
        ref={ref}
        className={cn(
          'rounded-xl transition-all duration-200',
          variants[variant],
          paddings[padding],
          hover && cn(
            'cursor-pointer',
            'hover:-translate-y-0.5',
            'hover:shadow-card-hover',
            'dark:hover:border-white/[0.1]',
            'dark:hover:bg-secondary-900/90'
          ),
          glow && 'dark:shadow-glow-sm',
          className
        )}
        {...props}
      />
    );
  }
);

Card.displayName = 'Card';

// Card Header
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
  compact?: boolean;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ className, compact = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex flex-col',
        compact ? 'space-y-0.5' : 'space-y-1',
        className
      )}
      {...props}
    />
  )
);

CardHeader.displayName = 'CardHeader';

// Card Title
interface CardTitleProps extends HTMLAttributes<HTMLHeadingElement> {
  as?: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6';
}

const CardTitle = forwardRef<HTMLHeadingElement, CardTitleProps>(
  ({ className, as: Component = 'h3', ...props }, ref) => (
    <Component
      ref={ref}
      className={cn(
        'text-base font-semibold tracking-tight',
        'text-gray-900 dark:text-secondary-100',
        className
      )}
      {...props}
    />
  )
);

CardTitle.displayName = 'CardTitle';

// Card Description
interface CardDescriptionProps extends HTMLAttributes<HTMLParagraphElement> {}

const CardDescription = forwardRef<HTMLParagraphElement, CardDescriptionProps>(
  ({ className, ...props }, ref) => (
    <p
      ref={ref}
      className={cn(
        'text-sm text-gray-500 dark:text-secondary-400',
        className
      )}
      {...props}
    />
  )
);

CardDescription.displayName = 'CardDescription';

// Card Content
interface CardContentProps extends HTMLAttributes<HTMLDivElement> {}

const CardContent = forwardRef<HTMLDivElement, CardContentProps>(
  ({ className, ...props }, ref) => (
    <div ref={ref} className={cn('', className)} {...props} />
  )
);

CardContent.displayName = 'CardContent';

// Card Footer
interface CardFooterProps extends HTMLAttributes<HTMLDivElement> {
  divider?: boolean;
}

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, divider = false, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        'flex items-center pt-3',
        divider && 'border-t border-gray-100 dark:border-white/[0.06] mt-3',
        className
      )}
      {...props}
    />
  )
);

CardFooter.displayName = 'CardFooter';

// Stat Card for dashboard metrics
interface StatCardProps extends HTMLAttributes<HTMLDivElement> {
  label: string;
  value: string | number;
  change?: {
    value: number;
    type: 'increase' | 'decrease' | 'neutral';
  };
  icon?: React.ReactNode;
}

const StatCard = forwardRef<HTMLDivElement, StatCardProps>(
  ({ className, label, value, change, icon, ...props }, ref) => {
    const changeColors = {
      increase: 'text-success-500',
      decrease: 'text-danger-500',
      neutral: 'text-secondary-400',
    };

    return (
      <Card ref={ref} className={cn('relative overflow-hidden', className)} {...props}>
        <div className="flex items-start justify-between">
          <div className="space-y-1">
            <p className="text-xs font-medium text-secondary-500 dark:text-secondary-400 uppercase tracking-wider">
              {label}
            </p>
            <p className="text-2xl font-semibold text-gray-900 dark:text-secondary-100 tracking-tight">
              {value}
            </p>
            {change && (
              <p className={cn('text-xs font-medium', changeColors[change.type])}>
                {change.type === 'increase' ? '+' : change.type === 'decrease' ? '-' : ''}
                {Math.abs(change.value)}%
              </p>
            )}
          </div>
          {icon && (
            <div className="p-2 rounded-lg bg-primary-500/10 dark:bg-primary-500/20 text-primary-500">
              {icon}
            </div>
          )}
        </div>
        {/* Subtle gradient accent */}
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-primary-500/20 to-transparent" />
      </Card>
    );
  }
);

StatCard.displayName = 'StatCard';

export { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, StatCard };
